import { Dexie } from 'dexie'
import { ForemanDB } from '../../types/IndexedDB/ForemanDB'
import { Employee } from '../../types/Employee/Employee'
import { ReworkTask } from '../../types/Work/WorkItemTask'
import { WorkItemTask } from '../../types/Work/WorkItemTask'
import { WorkItem } from '../../types/Work/WorkItem'
import dayjs from 'dayjs'
import { getTransactionTables } from '../../utils/MutationSnapshotUtils'
import { ChangeTracker, ChangeType } from '../../types/Cache/MutationSnapshot'
import { OfflineProgressRecord } from '../../types/Reports/ShiftEnd/ShiftEndWorkItems'

export const getConstraintsByWorkItemIdFB = async (db: ForemanDB, workItemId: number) => {
	const constraints = await db.constraints
		.filter((c) => c.workItemId === workItemId && !c.timeCleared)
		.toArray()
	return constraints
}

export const addConstraintFB = async (
	db: ForemanDB,
	workItemId: number,
	workerId: number,
	constraintComment: string,
	constraintAttachment: Blob | null,
) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, [
			'constraints',
			'constraintAttachments',
			'timeRecords',
			'tasks',
			'reworks',
			'directReports',
			'timesheets',
			'attendance',
			'workItems',
			'employee',
		]),
		async () => {
			const tx = Dexie.currentTransaction
			const { taskIds, reworkIds } = await db.setWorkItemHierarchyConstraint(workItemId, tx)

			await db.addConstraint(workItemId, constraintComment, workerId, constraintAttachment, tx)

			for (const taskId of taskIds) {
				await tx.table('tasks').update(taskId, {
					status: 'Inactive',
				})
			}
			for (const reworkId of reworkIds) {
				await tx.table('reworks').update(reworkId, {
					status: 'Inactive',
				})
			}
			const workers: Employee[] = await tx
				.table('directReports')
				.filter(
					(e: Employee) =>
						taskIds.includes(e.activeTaskId!) || reworkIds.includes(e.activeReworkId!),
				)
				.toArray()

			for (const worker of workers) {
				const task: WorkItemTask | ReworkTask | undefined = worker.activeTaskId
					? await tx.table('tasks').get(worker.activeTaskId)
					: await tx.table('reworks').get(worker.activeReworkId)
				await db.addTimeRecord(tx, worker.id, true, constraintComment, task)
				await tx.table('directReports').update(worker, {
					activeTaskId: undefined,
					activeReworkId: undefined,
				})
			}
			await tx.table('mutations').add({
				route: `/WorkItems/${workItemId}/Constraints`,
				method: 'POST',
				body: {
					comment: constraintComment,
					hasAttachment: constraintAttachment ? true : false,
					attachmentMimeType: constraintAttachment?.type,
				},
				title: `Added constraint: ${constraintComment}`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
		},
	)
}

export const updateWorkItemProgressFB = async (
	db: ForemanDB,
	workItemId: number,
	taskId: number,
	progress: number,
	foremanId: number,
) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, [
			'tasks',
			'workItems',
			'workItemAssignments',
			'reworkAssignments',
			'taskAssignments',
		]),
		async () => {
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}
			const workItem: WorkItem = await tx.table('workItems').get(workItemId)
			if (!workItem) {
				throw new Error('Work item not found')
			}
			const task: WorkItemTask = await tx.table('tasks').get(taskId)
			if (!task) {
				throw new Error('Task not found')
			}
			const taskQtyComplete = Number((task.quantityComplete + progress).toFixed(3))
			const taskQtyRemaining = Number((workItem.quantityBudgeted - taskQtyComplete).toFixed(3))
			const taskPercentComplete = Number((taskQtyComplete / workItem.quantityBudgeted).toFixed(3))
			const taskStatus = taskPercentComplete - 1 < 0.01 ? 'Complete' : null
			const taskPercentEarned = Number(
				((taskQtyComplete * task.creditRule!) / workItem.quantityBudgeted).toFixed(3),
			)

			await tx.table('tasks').update(taskId, {
				quantityComplete: taskQtyComplete,
				percentComplete: taskPercentComplete,
				status: taskStatus ?? task.status,
				quantityEarned: Number((taskPercentEarned * taskQtyComplete).toFixed(3)),
				quantityRemaining: taskQtyRemaining,
			})
			await db.calculateWorkItem(workItemId, tx)
			const logId = crypto.randomUUID()
			await tx
				.table('mutationSnapshot')
				.where('userId')
				.equals(foremanId)
				.modify((snapshot: any) => {
					const entityArray = snapshot['progressRecords'] as ChangeTracker<OfflineProgressRecord>[]
					if (!Array.isArray(entityArray)) {
						console.warn(`Entity type progressRecords is not an array in snapshot`)
						return
					}
					const existingProgressRecord = entityArray.find(
						(p: ChangeTracker<OfflineProgressRecord>) => p.entity.workItemTaskId === taskId,
					)
					if (existingProgressRecord) {
						existingProgressRecord.entity.enteredProgress += progress
						existingProgressRecord.entity.enteredDate = dayjs(
							new Date().toISOString().replace('Z', ''),
						).toDate()
						return
					}
					entityArray.push({
						entity: {
							workItemId: workItemId,
							workItemTaskId: taskId,
							enteredProgress: progress,
							enteredDate: dayjs(new Date().toISOString().replace('Z', '')).toDate(),
						},
						changeType: ChangeType.INSERTED,
						timestamp: new Date(),
						uid: crypto.randomUUID(),
						logId,
					})
				})

			await tx.table('mutations').add({
				route: `/WorkItems/${workItemId}/Tasks/${taskId}/Foreman/${foremanId}`,
				method: 'PATCH',
				body: {
					progress: progress,
					foremanId: foremanId,
				},
				title: `Updated progress for ${task.name}`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
		},
	)
}

export const clearConstraintFB = async (db: ForemanDB, constraintId: number) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, [
			'constraints',
			'constraintAttachments',
			'timeRecords',
			'tasks',
			'reworks',
			'directReports',
			'timesheets',
			'attendance',
			'workItems',
			'employee',
		]),
		async () => {
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}
			const constraint = await tx.table('constraints').where('id').equals(constraintId).first()
			if (!constraint) {
				throw new Error('Constraint not found')
			}
			await tx.table('constraints').update([constraint.id, constraint.workItemId], {
				timeCleared: dayjs(new Date().toISOString().replace('Z', '')).toDate(),
			})

			let childrenWorkItemsIds: number[] = (
				await tx.table('workItems').filter((wi) => wi.parentId === constraint.workItemId).toArray()
			).map((c: WorkItem) => c.id)
			while (childrenWorkItemsIds.length > 0) {
				for (const childWorkItem of childrenWorkItemsIds) {
					await tx.table('workItems').update(childWorkItem, {
						status: 'Inactive',
					})
				}
				childrenWorkItemsIds = (
					await tx
						.table('workItems')
						.filter((wi) => childrenWorkItemsIds.some((c) => c === wi.parentId))
						.toArray()
				).map((c: WorkItem) => c.id)
			}
			await tx.table('mutations').add({
				route: `/Constraints/${constraintId}`,
				method: 'DELETE',
				body: {},
				title: `Cleared constraint: ${constraint.comments}`,
				createdAt: new Date(),
			})
		},
	)
}
